// Player HUD — shows encountered players with colour, name, and power score.
//
// Information is gated by relationship level (diplomacy / espionage):
// - full:   exact score + category breakdown (alliance, high-intel espionage)
// - estimate: fuzzy score ±10 % (neutral / low-intel espionage)
// - minimal:  only player name + colour (war, not yet encountered)
//
// Rendered as a horizontal pill strip so it occupies the strip between the
// ResourceBar and the side rails (DoctrinePanel / InfoPanel). Earlier
// versions rendered a vertical card list at the top-left corner, which was
// completely obscured by the DoctrinePanel's z-40 overlay.

import { PowerScoreBar } from './PowerScoreBar.js';
import { cn } from './lib/utils.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type InfoLevel = 'full' | 'estimate' | 'minimal';

export interface PowerBreakdown {
  military: number;
  economic: number;
  prestige: number;
  territory: number;
  doctrine: number;
}

export interface PlayerHudEntry {
  readonly playerId: number;
  readonly name: string;
  /** CSS hex colour e.g. "#ff4444". */
  readonly color: string;
  /** 0–100 relative power score. */
  readonly powerScore: number;
  readonly infoLevel: InfoLevel;
  /** Only available at "full" info level. */
  readonly breakdown?: PowerBreakdown;
}

export interface PlayerHudProps {
  readonly players: readonly PlayerHudEntry[];
  readonly className?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PlayerHud({ players, className }: PlayerHudProps) {
  if (players.length === 0) return null;

  return (
    <aside
      aria-label="Players"
      data-testid="player-hud"
      data-appdom-id="player-hud"
      data-appdom-type="List"
      data-appdom-scope="main"
      data-appdom-label="Players"
      className={cn(
        'flex flex-row items-center gap-2 rounded-md border border-border/70 bg-card/88 px-3 py-1 backdrop-blur-sm',
        className,
      )}
    >
      <h3 className="shrink-0 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        Players
      </h3>
      {/* biome-ignore lint/a11y/noNoninteractiveTabindex: a sideways-scrolling list must be keyboard reachable */}
      <div className="flex flex-row items-center gap-3 overflow-x-auto" tabIndex={0}>
        {players.map((p) => (
          <PlayerPill key={p.playerId} entry={p} />
        ))}
      </div>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Pill (compact horizontal row)
// ---------------------------------------------------------------------------

function PlayerPill({ entry: p }: { entry: PlayerHudEntry }) {
  return (
    <div
      className="flex shrink-0 items-center gap-1.5 rounded border border-border/40 bg-card/55 px-2 py-0.5 text-xs"
      title={
        p.infoLevel === 'full' && p.breakdown
          ? `Mil ${p.breakdown.military} · Eco ${p.breakdown.economic} · Pres ${p.breakdown.prestige} · Ter ${p.breakdown.territory} · Doc ${p.breakdown.doctrine}`
          : p.name
      }
    >
      <span
        className="inline-block size-2.5 shrink-0 rounded-full border border-border/60"
        style={{ backgroundColor: p.color }}
      />
      <span className="font-medium text-foreground whitespace-nowrap">
        {p.name}
        {p.infoLevel === 'estimate' && (
          <span className="ml-0.5 text-[10px] text-muted-foreground">~</span>
        )}
      </span>
      {p.infoLevel !== 'minimal' && <PowerScoreBar score={p.powerScore} className="w-16 gap-1" />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Legacy full-detail panel (retained for callers that opt in via className
// detection — see App.tsx for the only consumer). Exported separately so
// the in-shell layout can pick which view to use.
// ---------------------------------------------------------------------------

export interface PlayerHudDetailedProps {
  readonly players: readonly PlayerHudEntry[];
  readonly className?: string;
}

export function PlayerHudDetailed({ players, className }: PlayerHudDetailedProps) {
  if (players.length === 0) return null;

  return (
    <aside
      aria-label="Players"
      data-testid="player-hud"
      data-appdom-id="player-hud"
      data-appdom-type="List"
      data-appdom-scope="main"
      data-appdom-label="Players"
      className={cn(
        'flex flex-col gap-2 rounded-lg border border-border/70 bg-card/88 p-3 backdrop-blur-sm',
        className,
      )}
    >
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Players
      </h3>
      {players.map((p) => (
        <PlayerRow key={p.playerId} entry={p} />
      ))}
    </aside>
  );
}

function PlayerRow({ entry: p }: { entry: PlayerHudEntry }) {
  return (
    <div className="flex flex-col gap-1 rounded-md border border-border/50 bg-card/60 px-2.5 py-2">
      <div className="flex items-center gap-2">
        <span
          className="inline-block size-3 rounded-full border border-border/60"
          style={{ backgroundColor: p.color }}
        />
        <span className="text-sm font-medium text-foreground">{p.name}</span>
        {p.infoLevel === 'estimate' && (
          <span className="ml-auto text-[10px] text-muted-foreground">~</span>
        )}
      </div>
      {p.infoLevel !== 'minimal' && <PowerScoreBar score={p.powerScore} className="mt-0.5" />}
      {p.infoLevel === 'full' && p.breakdown && (
        <div className="mt-1 flex flex-col gap-0.5 border-t border-border/40 pt-1">
          <BreakdownRow label="Military" value={p.breakdown.military} />
          <BreakdownRow label="Economic" value={p.breakdown.economic} />
          <BreakdownRow label="Prestige" value={p.breakdown.prestige} />
          <BreakdownRow label="Territory" value={p.breakdown.territory} />
          <BreakdownRow label="Doctrine" value={p.breakdown.doctrine} />
        </div>
      )}
    </div>
  );
}

function BreakdownRow({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-center justify-between text-[11px] tabular-nums">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}
